<script setup lang="ts">
import type { Locale } from '~/types/locale'

const {
  $i18n: { locale },
  $config: {
    public: { storeUrl, exponea: exponeaConfig },
  },
  hooks,
} = useNuxtApp()
const lang = locale.value.substring(0, 2)
const router = useRouter()
const route = useRoute()
const { pageData, hrefLangs } = storeToRefs(usePageStore())
const { trackPageVisit } = useExponea()

const idMap = {
  fr: 'X9C491DF0B7C883AD6285ED873A1C7D73',
  de: 'X68891C5C5C38C7B89516EA675C239539',
}

const id = idMap[lang] ?? null

const image = getBynderImageObject(pageData.value?.image)
const ogImage =
  (image && [
    {
      hid: 'og:image',
      name: 'og:image',
      content: image.url || null,
    },
    {
      hid: 'og:image:width',
      name: 'og:image:width',
      content: image.width || 600,
    },
    {
      hid: 'og:image:height',
      name: 'og:image:height',
      content: image.height || 600,
    },
  ]) ||
  []

const { projectToken } = exponeaConfig?.[locale.value as Locale] || {}

useHead({
  title: () => pageData.value?.title,
  htmlAttrs: {
    lang: locale.value,
    class: computed(() => (route.meta.layout === 'storefront' ? 'theme--storefront' : 'theme--base')),
  },
  link: () => [
    ...(hrefLangs.value || []),
    {
      rel: 'icon',
      type: 'image/png',
      href: '/favicon-96x96.png',
      sizes: '96x96',
      hid: 'icon-96',
    },
    {
      rel: 'icon',
      type: 'image/svg+xml',
      href: '/favicon.svg',
      hid: 'icon-svg',
    },
    {
      rel: 'shortcut icon',
      href: '/favicon.ico',
      hid: 'shortcut-icon',
    },
    {
      rel: 'apple-touch-icon',
      sizes: '180x180',
      href: '/apple-touch-icon.png',
      hid: 'apple-touch-icon',
    },
    {
      hid: 'bynder-preconnect',
      rel: 'preconnect',
      href: 'https://art.my-jewellery.com',
      crossorigin: true,
    },
    {
      hid: 'exponea-preconnect',
      rel: 'preconnect',
      href: 'https://exp-api.my-jewellery.com',
      crossorigin: true,
    },
    {
      hid: 'canonical',
      rel: 'canonical',
      href: pageData.value?.canonical || `${storeUrl}${route.fullPath}` || null,
    },
  ],
  meta: () => [
    { charset: 'utf-8' },
    {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1, viewport-fit=cover',
    },
    {
      name: 'msapplication-TileColor',
      content: '#fbdbe7',
    },
    {
      name: 'apple-mobile-web-app-title',
      content: 'My Jewellery',
    },
    {
      hid: 'description',
      name: 'description',
      content: pageData.value?.description,
    },
    {
      hid: 'og:description',
      name: 'og:description',
      content: pageData.value?.description,
    },
    {
      hid: 'og:title',
      name: 'og:title',
      content: pageData.value?.title,
    },
    {
      hid: 'title',
      name: 'title',
      content: pageData.value?.title,
    },
    {
      hid: 'keywords',
      name: 'keywords',
      content: pageData.value?.metaKeywords,
    },
    {
      hid: 'og:site_name',
      name: 'og:site_name',
      content: 'My Jewellery',
    },
    {
      hid: 'robots',
      name: 'robots',
      content: `${pageData.value?.robotsIndex?.selectionValues[0].label || 'index'}, ${
        pageData.value?.robotsFollow?.selectionValues[0].label || 'follow'
      }`.trim(),
    },
    ...ogImage,
  ],
  script: [
    {
      hid: 'exponea',
      innerHTML: `
        var myjExpFbpCk = document.cookie.match('(^|;)\\s*_fbp\\s*=\\s*([^;]+)')?.pop() || undefined;
        var myjExpScid = document.cookie.match('(^|;)\\s*_scid\\s*=\\s*([^;]+)')?.pop() || undefined;
        var myjExpTtp = document.cookie.match('(^|;)\\s*_ttp\\s*=\\s*([^;]+)')?.pop() || undefined;
        var myjExpUrl = new URL(window.location.href);
        var myjExpFbclid = myjExpUrl.searchParams.get("fbclid") || undefined;
        var myjExpScCid = myjExpUrl.searchParams.get("ScCid") || undefined;
        var myjExpTtclid = myjExpUrl.searchParams.get("ttclid") || undefined;
        var myjExpFbc=void 0;if((!myjExpFbpCk||"undefined"==typeof myjExpFbpCk||myjExpFbpCk.length<6)&&myjExpFbclid&&"string"==typeof myjExpFbclid&&myjExpFbclid.length>6)myjExpFbc="fb.1."+Date.now().toString()+"."+myjExpFbclid;
        var myjExpInfDev="desktop";navigator.userAgent.match(/Mobi/)&&(myjExpInfDev="mobile");var myjConn=navigator.connection||navigator.mozConnection||navigator.webkitConnection;myjConn&&"cellular"===myjConn.type&&(myjExpInfDev="mobile");var myjOr=screen.msOrientation||(screen.orientation||screen.mozOrientation||{}).type;"portrait-primary"==myjOr&&(myjExpInfDev="mobile"),"screen"in window&&window.screen.width<992&&(myjExpInfDev="mobile");
        !function(e,n,t,i,r,o){function s(e){if("number"!=typeof e)return e;var n=new Date;return new Date(n.getTime()+1e3*e)}var a=4e3,c="xnpe_async_hide";function p(e){return e.reduce((function(e,n){return e[n]=function(){e._.push([n.toString(),arguments])},e}),{_:[]})}function m(e,n,t){var i=t.createElement(n);i.src=e;var r=t.getElementsByTagName(n)[0];return r.parentNode.insertBefore(i,r),i}function u(e){return"[object Date]"===Object.prototype.toString.call(e)}o.target=o.target||"https://api.exponea.com",o.file_path=o.file_path||o.target+"/js/exponea.min.js",r[n]=p(["anonymize","initialize","identify","getSegments","update","track","trackLink","trackEnhancedEcommerce","getHtml","showHtml","showBanner","showWebLayer","ping","getAbTest","loadDependency","getRecommendation","reloadWebLayers","_preInitialize","_initializeConfig"]),r[n].notifications=p(["isAvailable","isSubscribed","subscribe","unsubscribe"]),r[n].segments=p(["subscribe"]),r[n]["snippetVersion"]="v2.7.0",function(e,n,t){e[n]["_"+t]={},e[n]["_"+t].nowFn=Date.now,e[n]["_"+t].snippetStartTime=e[n]["_"+t].nowFn()}(r,n,"performance"),function(e,n,t,i,r,o){e[r]={sdk:e[i],sdkObjectName:i,skipExperiments:!!t.new_experiments,sign:t.token+"/"+(o.exec(n.cookie)||["","new"])[1],path:t.target}}(r,e,o,n,i,RegExp("__exponea_etc__"+"=([\\w-]+)")),function(e,n,t){m(e.file_path,n,t)}(o,t,e),function(e,n,t,i,r,o,p){if(e.new_experiments){!0===e.new_experiments&&(e.new_experiments={});var l,f=e.new_experiments.hide_class||c,_=e.new_experiments.timeout||a,g=encodeURIComponent(o.location.href.split("#")[0]);e.cookies&&e.cookies.expires&&("number"==typeof e.cookies.expires||u(e.cookies.expires)?l=s(e.cookies.expires):e.cookies.expires.tracking&&("number"==typeof e.cookies.expires.tracking||u(e.cookies.expires.tracking))&&(l=s(e.cookies.expires.tracking))),l&&l<new Date&&(l=void 0);var d=e.target+"/webxp/"+n+"/"+o[t].sign+"/modifications.min.js?http-referer="+g+"&timeout="+_+"ms"+(l?"&cookie-expires="+Math.floor(l.getTime()/1e3):"");"sync"===e.new_experiments.mode&&o.localStorage.getItem("__exponea__sync_modifications__")?function(e,n,t,i,r){t[r][n]="<"+n+' src="'+e+'"></'+n+">",i.writeln(t[r][n]),i.writeln("<"+n+">!"+r+".init && document.writeln("+r+"."+n+'.replace("/'+n+'/", "/'+n+'-async/").replace("><", " async><"))</'+n+">")}(d,n,o,p,t):function(e,n,t,i,r,o,s,a){o.documentElement.classList.add(e);var c=m(t,i,o);function p(){r[a].init||m(t.replace("/"+i+"/","/"+i+"-async/"),i,o)}function u(){o.documentElement.classList.remove(e)}c.onload=p,c.onerror=p,r.setTimeout(u,n),r[s]._revealPage=u}(f,_,d,n,o,p,r,t)}}(o,t,i,0,n,r,e),function(e,n,t){var i;e[n]._initializeConfig(t),(null===(i=t.experimental)||void 0===i?void 0:i.non_personalized_weblayers)&&e[n]._preInitialize(t),e[n].start=function(i){i&&Object.keys(i).forEach((function(e){return t[e]=i[e]})),e[n].initialize(t)}}(r,n,o)}(document,"exponea","script","webxpClient",window,{
          target: "https://exp-api.my-jewellery.com",
          token: "${projectToken}",
          experimental: {
              non_personalized_weblayers: true
          },
          new_experiments: { mode: "async", timeout: 1000 },
          track: {
            visits: false
          },
          service_worker_path: "/service-worker.js",
          utm_always: true,
          utm_params: [
            'utm_source',
            'utm_medium',
            'utm_campaign',
            'utm_term',
            'utm_content',
            'utm_id',
            'gclid',
            'fbclid',
            'ScCid',
            'ttclid'
          ],
          ping: {
            enabled: true,
            properties: {
              device_type: myjExpInfDev,
              fbclid: myjExpFbclid,
              fbc: myjExpFbc,
              fbp: myjExpFbpCk,
              sccid: myjExpScCid,
              scid: myjExpScid,
              ttp: myjExpTtp,
              language: navigator.language,
              languages: navigator.languages,
              user_agent: navigator.userAgent
            }
          }
        });
        window.exponea?.start({
          spa_reloading: { banners: false, visits: false },
          webOptimization: {
            webLayers: false
          }
        })
      `,
      async: false,
      defer: false,
      type: 'text/javascript',
    },
    ...(id
      ? [
          {
            src: `//widgets.trustedshops.com/js/${id}.js`,
            defer: true,
            'data-mobile-position': 'right',
          },
        ]
      : []),
  ],
})

const firstTime = ref(true)
const tracked = ref(false)
hooks.hook('page:finish', () => {
  // First time Exponea does its own thing
  // After that we reload the weblayers when the page is loaded
  if (firstTime.value) {
    firstTime.value = false
  } else {
    // If the page was not already tracked on a query change, track it now
    if (!tracked.value) {
      trackPageVisit()
    }
    window.exponea?.reloadWebLayers()
  }
  tracked.value = false
})

// Track page visit on pagination change
router.afterEach((to, from) => {
  if (to.path === from.path && JSON.stringify(to.query) !== JSON.stringify(from.query)) {
    trackPageVisit()
    tracked.value = true
  }
})

const exponeaUserId = useCookie('__exponea_etc__').value
const datadogCookie = useCookie('datadogmonitoring').value

onServerPrefetch(() => {
  const platform = useState('platform')

  platform.value = useRequestHeader('sec-ch-ua-platform')
})

onMounted(() => {
  document.documentElement.style.setProperty(
    '--gutter-width',
    window.innerWidth - document.documentElement.clientWidth + 'px',
  )
  useTrustedWidgetShops()
})

onNuxtReady(() => {
  if (datadogCookie) {
    return
  }
  const { customer } = storeToRefs(useCustomerStore())
  let email = customer.value?.email ?? ''
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  if (emailRegex.test(email)) {
    window.exponea?.identify({ registered: email })
  } else if (exponeaUserId) {
    window.exponea?.identify({ cookie: exponeaUserId })
  }
  // Reload web layers because they are disabled on initial load
  window.exponea?.reloadWebLayers()
})
</script>

<template>
  <NuxtLayout>
    <UApp>
      <NuxtPage />
    </UApp>
  </NuxtLayout>
</template>
